import { useEffect, useState } from 'react';
import { api } from '@/lib/api-client';
import '../../admin.css';

export interface AssignUsersModalProps {
  roleId: string;
  onClose: () => void;
  onAssigned: () => void;
}

interface LocalUser {
  id: string;
  name: string;
  email: string;
  isActive: boolean;
}

interface SuiteUser {
  suiteUserId: string;
  email: string;
  name: string;
  local: boolean;
  localUserId: string | null;
}

type PickerRow =
  | { source: 'local'; id: string; name: string; email: string }
  | { source: 'suite'; id: string; name: string; email: string; suiteUserId: string; isLocal: boolean; localUserId: string | null };

export function AssignUsersModal({ roleId, onClose, onAssigned }: AssignUsersModalProps) {
  const [showSuite, setShowSuite] = useState(false);
  const [rows, setRows] = useState<PickerRow[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setSelected(new Set());
    if (showSuite) {
      api.get<{ users: SuiteUser[] }>('/users/suite').then(
        (b) => setRows(
          b.users.map((u) => ({
            source: 'suite' as const,
            id: u.suiteUserId,
            name: u.name,
            email: u.email,
            suiteUserId: u.suiteUserId,
            isLocal: u.local,
            localUserId: u.localUserId,
          })),
        ),
        () => setRows([]),
      );
    } else {
      api.get<{ users: LocalUser[] }>('/users').then(
        // F-CAPTURE-043 — offer only active users: a retired row (same name and email as the
        // live one) or an inactive system identity can hold a role nothing ever reads.
        (b) => setRows(
          b.users.filter((u) => u.isActive).map((u) => ({
            source: 'local' as const,
            id: u.id,
            name: u.name,
            email: u.email,
          })),
        ),
        () => setRows([]),
      );
    }
  }, [showSuite]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function submit() {
    setSubmitting(true);
    try {
      const picked = rows.filter((r) => selected.has(r.id));
      // For Suite-source rows that aren't already local, invite first to get
      // a Capture-local userId. Already-local Suite rows short-circuit
      // straight to the localUserId we already have.
      const userIds: string[] = [];
      for (const r of picked) {
        if (r.source === 'local') {
          userIds.push(r.id);
          continue;
        }
        if (r.isLocal && r.localUserId) {
          userIds.push(r.localUserId);
          continue;
        }
        const invited = await api.post<{ user: { id: string } }>('/users/invite', {
          suiteUserId: r.suiteUserId,
        });
        userIds.push(invited.user.id);
      }
      await api.post(`/roles/${roleId}/users`, { userIds });
      onAssigned();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div
        className="dialog-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Assign users"
      >
        <h2 className="dialog-title">Assign users</h2>
        <label
          style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            checked={showSuite}
            onChange={(e) => setShowSuite(e.target.checked)}
          />
          <span>Show users from Suite</span>
        </label>
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px' }}>
          {rows.map((u) => (
            <li key={u.id} style={{ marginBottom: 8 }}>
              <label aria-label={`Select ${u.name}`} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={selected.has(u.id)}
                  onChange={() => toggle(u.id)}
                />
                <span>{u.name}</span>
                <span style={{ color: 'var(--color-text-muted, #6b7280)', fontSize: '0.875rem' }}>({u.email})</span>
                {u.source === 'suite' && u.isLocal && (
                  <span style={{ color: 'var(--color-text-muted, #6b7280)', fontSize: '0.75rem', fontStyle: 'italic' }}>
                    already in Capture
                  </span>
                )}
              </label>
            </li>
          ))}
        </ul>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="rb-btn rb-btn-save"
            onClick={submit}
            disabled={submitting || selected.size === 0}
          >
            Assign {selected.size}
          </button>
          <button type="button" className="rb-btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
